import { Prompt, PromptType } from "common/models/types";

/**
 * 어드민 프롬프트 목록의 "타입별 지연 로드" 상태 전이 순수 로직.
 *
 * 평면 offset 페이지네이션 + 클라이언트 그룹핑 구조에서는 한 타입의 버전이 많으면
 * 다른 종류가 첫 페이지에 들어오지 못해 화면에 누락되는 문제가 있었다.
 * 이 모듈은 그 재설계(종류는 항상 노출, 버전은 타입별로 따로 로드)의 상태 계산을
 * React 외부의 순수 함수로 분리해 단위 테스트 가능하게 한 것이다.
 *
 * 설계 배경: docs/ai-discussions/20260524-admin-prompt-list-type-lazy-load.md
 */

// 타입별 한 페이지 크기
export const PAGE = 20;

// 카운트 자가 판정 임계값: 서로 다른 두 타입의 totalItems가 모두 같고
// 이 값을 넘으면 "필터 미반영(전체 카운트)"으로 의심한다.
export const COUNT_RELIABLE_THRESHOLD = 100;

export interface TypeListState {
  items: Prompt[];
  offset: number;
  hasMore: boolean;
  isLoading: boolean;
  totalItems: number;
  error?: boolean; // 페이지 로드 실패 표시
}

export const EMPTY_TYPE_STATE: TypeListState = {
  items: [],
  offset: 0,
  hasMore: true,
  isLoading: false,
  totalItems: 0,
};

// API 응답 meta 중 이 모듈이 사용하는 부분
export interface PageMeta {
  totalItems?: number;
  hasMore?: boolean;
}

export interface CountResult {
  type: PromptType;
  total: number;
}

/**
 * 기존 loadPrompts가 응답마다 채우던 기본값 정규화를 보존한다.
 * (회귀 방지: PromptItem이 undefined version/isActive 등을 만나지 않도록)
 */
export const normalizePrompt = (prompt: Prompt): Prompt => ({
  ...prompt,
  version: prompt.version || "v1.0.0",
  isActive: prompt.isActive ?? false,
  description: prompt.description || "",
  promptTemplate: prompt.promptTemplate || "",
  promptType: prompt.promptType || PromptType.FREE_QUESTION,
});

/**
 * 진입 시 종류별 카운트(allSettled 결과)를 typeCounts 맵으로 변환한다.
 * - total > 0 인 종류만 보유(빈 종류 숨김)
 * - rejected 개수는 failed로 집계(부분 실패 표시용)
 */
export const buildTypeCounts = (
  results: PromiseSettledResult<CountResult>[]
): { counts: Partial<Record<PromptType, number>>; failed: number } => {
  const counts: Partial<Record<PromptType, number>> = {};
  let failed = 0;
  results.forEach((r) => {
    if (r.status === "fulfilled") {
      if (r.value.total > 0) counts[r.value.type] = r.value.total;
    } else {
      failed += 1;
    }
  });
  return { counts, failed };
};

/**
 * 타입별 페이지를 기존 상태에 병합한다.
 * - isInitial이면 0부터 새로 채움, 아니면 기존 items 뒤에 이어붙임
 * - meta가 없으면 incoming 길이로 hasMore 추론(P3 보강)
 * - 각 항목은 normalizePrompt 적용
 */
export const appendPage = (
  prev: TypeListState | undefined,
  incoming: Prompt[],
  isInitial: boolean,
  meta?: PageMeta,
  fallbackTotal?: number
): TypeListState => {
  const normalized = incoming.map(normalizePrompt);
  const prevItems = isInitial || !prev ? [] : prev.items;
  const items = [...prevItems, ...normalized];
  return {
    items,
    offset: items.length,
    hasMore: meta?.hasMore ?? normalized.length === PAGE,
    isLoading: false,
    totalItems: meta?.totalItems ?? prev?.totalItems ?? fallbackTotal ?? items.length,
    error: false,
  };
};

/**
 * CRUD 후 특정 타입의 카운트를 갱신한다. total이 0이면 키를 삭제(헤더 숨김).
 */
export const applyCountAfterCrud = (
  counts: Partial<Record<PromptType, number>>,
  type: PromptType,
  total: number
): Partial<Record<PromptType, number>> => {
  const next = { ...counts };
  if (total > 0) next[type] = total;
  else delete next[type];
  return next;
};

/**
 * 카운트 신뢰성 자가 판정.
 * 서로 다른 종류의 totalItems가 모두 동일하고 임계값을 넘으면
 * "promptType 필터 미반영(전체 카운트)"으로 의심하고 false를 반환한다.
 * 판정에 쓸 양수 카운트가 2개 미만이면 단정 불가 → true(정상)로 간주.
 */
export const detectCountReliable = (totals: number[]): boolean => {
  const positive = totals.filter((t) => t > 0);
  if (positive.length < 2) return true;
  const allSame = positive.every((t) => t === positive[0]);
  return !(allSame && positive[0] > COUNT_RELIABLE_THRESHOLD);
};
